import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Button } from "primereact/button";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Toast } from "primereact/toast";
import { Paginator } from "primereact/paginator";
import DefMembershipActions from "../../Redux/actions/defMembership-actions";
import BusinessCustomersActions from "../../Redux/transition/customers/business-customer.reducer";
import PlanGroupActions from "../../Redux/transition/plan-group/plan-group.reducer";
import ReportConfigurationActions from "../../Redux/transition/report-configuration/report-configuration.reducer";
import CustomDataTable from "../../Components/CustomDataTable";
import StatusBadge from "../../Components/StatusBadge";
import {
  buildCreateReportConfigurationPayload,
  getReportConfigurationColumnSections,
  hasAnyReportColumns,
  mapReportConfigurationsForTable,
  ownerContributionPlanGroupsOverlap,
  tagGroupsConflict,
} from "../../constants/reportConfiguration";
import CreateReportConfigModal from "./CreateReportConfigModal";
import "../../Styles/reportConfigs.scss";
import "../../Styles/pages/product.scss";
import "../../Styles/pages/customers.scss";

const REPORT_CONFIG_ROWS = 15;
const CONTRIBUTIONS_REPORT_MOBILE_BREAKPOINT = 1050;

const ACTION_TOOLTIP_OPTIONS = {
  position: "bottom",
  mouseTrack: true,
  mouseTrackTop: 15,
};

const toPlain = (data) => {
  if (data == null) return data;
  if (typeof data.asMutable === "function") return data.asMutable({ deep: true });
  if (typeof data.toJS === "function") return data.toJS();
  return data;
};

const normalizeSegmentList = (data) => {
  const plain = toPlain(data);
  if (!plain) return [];
  if (Array.isArray(plain)) return plain;
  if (Array.isArray(plain.segments)) return plain.segments;
  if (Array.isArray(plain.content)) return plain.content;
  return [];
};

const normalizeTagGroups = (data) => {
  const plain = toPlain(data);
  if (!plain) return [];
  if (Array.isArray(plain.groups)) return plain.groups;
  if (Array.isArray(plain)) return plain;
  return [];
};

const initialCreateForm = {
  name: "",
  segmentId: "",
  sheetTagGroupId: "",
  sectionTagGroupId: "",
  planIds: [],
  ownerPlanGroupIds: [],
  contributionPlanGroupIds: [],
};

const ContributionsReport = () => {
  const { t } = useTranslation("common");
  const dispatch = useDispatch();
  const toast = useRef(null);
  const prevCreatingRef = useRef(false);
  const prevGeneratingIdRef = useRef(null);
  const prevDeletingIdRef = useRef(null);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createForm, setCreateForm] = useState(initialCreateForm);
  const [createSubmitAttempted, setCreateSubmitAttempted] = useState(false);
  const [first, setFirst] = useState(0);
  const [isMobileStack, setIsMobileStack] = useState(
    () =>
      typeof window !== "undefined" &&
      window.innerWidth <= CONTRIBUTIONS_REPORT_MOBILE_BREAKPOINT
  );

  const defMembership = useSelector((state) => state.defMembership.defMembership);
  const businessId = defMembership?.business?.id;
  const businessCurrency = defMembership?.business?.currency;

  const reportConfigurations = useSelector(
    (state) => state.reportConfiguration.reportConfigurations
  );
  const fetchingReportConfigurations = useSelector(
    (state) => state.reportConfiguration.fetchingReportConfigurations
  );
  const creatingReportConfiguration = useSelector(
    (state) => state.reportConfiguration.creatingReportConfiguration
  );
  const errorCreateReportConfiguration = useSelector(
    (state) => state.reportConfiguration.errorCreateReportConfiguration
  );
  const generatingReportConfigurationId = useSelector(
    (state) => state.reportConfiguration.generatingReportConfigurationId
  );
  const deletingReportConfigurationId = useSelector(
    (state) => state.reportConfiguration.deletingReportConfigurationId
  );
  const errorGenerateReportConfiguration = useSelector(
    (state) => state.reportConfiguration.errorGenerateReportConfiguration
  );
  const errorDeleteReportConfiguration = useSelector(
    (state) => state.reportConfiguration.errorDeleteReportConfiguration
  );

  const segmentsData = useSelector((state) => state.businessCustomers.segmentSuccess);
  const tagGroupsGraphed = useSelector((state) => state.businessCustomers.tagGroupsGraphed);
  const fetchingSegment = useSelector((state) => state.businessCustomers.fetchingSegment);
  const fetchingTagGroupsGraphed = useSelector(
    (state) => state.businessCustomers.fetchingTagGroupsGraphed
  );
  const planGroupsGraphedAll = useSelector(
    (state) => state.planGroups.planGroupsGraphedAll
  );
  const fetchingPlans = useSelector(
    (state) => state.planGroups.fetchingplanGroupsGraphedAll
  );

  const getDefMembership = useCallback(
    () => dispatch(DefMembershipActions.defMembershipRequest()),
    [dispatch]
  );
  const getReportConfigurations = useCallback(
    (id) => dispatch(ReportConfigurationActions.reportConfigurationsRequest(id)),
    [dispatch]
  );
  const getSegment = useCallback(
    (id) =>
      dispatch(
        BusinessCustomersActions.fetchingSegmentRequest(id, {
          page: 0,
          size: 1000,
          sort: "createdDate,desc",
        })
      ),
    [dispatch]
  );
  const getTagGroupsGraphed = useCallback(
    (id) => dispatch(BusinessCustomersActions.tagGroupGraphedRequest(id)),
    [dispatch]
  );
  const getPlanGroupsGraphedAll = useCallback(
    (id) => dispatch(PlanGroupActions.planGroupGraphedAllRequest(id)),
    [dispatch]
  );

  useEffect(() => {
    if (!defMembership) {
      getDefMembership();
    }
  }, [defMembership, getDefMembership]);

  useEffect(() => {
    dispatch(ReportConfigurationActions.generateReportConfigurationClearError());
    dispatch(ReportConfigurationActions.deleteReportConfigurationClearError());
  }, [dispatch]);

  useEffect(() => {
    const mediaQuery = window.matchMedia(
      `(max-width: ${CONTRIBUTIONS_REPORT_MOBILE_BREAKPOINT}px)`
    );
    const onBreakpointChange = (event) => setIsMobileStack(event.matches);
    onBreakpointChange(mediaQuery);
    mediaQuery.addEventListener("change", onBreakpointChange);
    return () => mediaQuery.removeEventListener("change", onBreakpointChange);
  }, []);

  useEffect(() => {
    if (!businessId) return;
    getReportConfigurations(businessId);
    getSegment(businessId);
    getTagGroupsGraphed(businessId);
    getPlanGroupsGraphedAll(businessId);
  }, [
    businessId,
    getReportConfigurations,
    getSegment,
    getTagGroupsGraphed,
    getPlanGroupsGraphedAll,
  ]);

  const segments = useMemo(
    () => normalizeSegmentList(segmentsData),
    [segmentsData]
  );

  const tagGroups = useMemo(
    () => normalizeTagGroups(tagGroupsGraphed),
    [tagGroupsGraphed]
  );

  const businessPlanGroups = useMemo(() => {
    const plain = toPlain(planGroupsGraphedAll);
    const groups = plain?.groups;
    if (!Array.isArray(groups)) return [];
    return groups
      .map((groupItem) => {
        const planGroup = groupItem.group;
        const id = planGroup?.id ?? groupItem.id;
        const name = planGroup?.name ?? groupItem.name ?? "";
        return id != null ? { id, name } : null;
      })
      .filter(Boolean);
  }, [planGroupsGraphedAll]);

  const businessPlans = useMemo(() => {
    const plain = toPlain(planGroupsGraphedAll);
    const groups = plain?.groups;
    if (!Array.isArray(groups)) return [];
    return groups.flatMap((groupItem) => {
      const plansInGroup = groupItem.plans || [];
      const planGroup = groupItem.group;
      return plansInGroup.map((plan) => ({
        ...plan,
        groupName: planGroup?.name ?? plan.groupName ?? "",
        groupId: planGroup?.id ?? plan.groupId ?? plan.group,
      }));
    });
  }, [planGroupsGraphedAll]);

  const fetchingOptions = fetchingSegment || fetchingTagGroupsGraphed || fetchingPlans;

  const closeCreateModal = useCallback(() => {
    setCreateModalOpen(false);
    setCreateForm(initialCreateForm);
    setCreateSubmitAttempted(false);
  }, []);

  const openCreateModal = useCallback(() => {
    setCreateForm(initialCreateForm);
    setCreateSubmitAttempted(false);
    setCreateModalOpen(true);
  }, []);

  const handleCreateFormChange = useCallback((updates) => {
    setCreateForm((prev) => ({ ...prev, ...updates }));
  }, []);

  const handleGenerateReport = useCallback(
    (configId, configName) => {
      if (generatingReportConfigurationId != null) return;
      dispatch(
        ReportConfigurationActions.generateReportConfigurationRequest(configId, configName)
      );
    },
    [dispatch, generatingReportConfigurationId]
  );

  const handleDeleteReport = useCallback(
    (configId, configName) => {
      if (deletingReportConfigurationId != null) return;
      const label = configName || String(configId);
      confirmDialog({
        message: t("reportConfigs.delete_confirm", { name: label }),
        header: t("reportConfigs.delete"),
        icon: "pi pi-exclamation-triangle",
        acceptClassName: "p-button-danger",
        accept: () =>
          dispatch(
            ReportConfigurationActions.deleteReportConfigurationRequest(
              configId,
              businessId
            )
          ),
      });
    },
    [dispatch, deletingReportConfigurationId, businessId, t]
  );

  const handleCreateSubmit = useCallback(() => {
    setCreateSubmitAttempted(true);
    const name = String(createForm.name || "").trim();
    if (
      !name ||
      !createForm.segmentId ||
      !hasAnyReportColumns(createForm) ||
      ownerContributionPlanGroupsOverlap(
        createForm.ownerPlanGroupIds,
        createForm.contributionPlanGroupIds
      ) ||
      tagGroupsConflict(createForm.sheetTagGroupId, createForm.sectionTagGroupId)
    ) {
      return;
    }
    dispatch(
      ReportConfigurationActions.createReportConfigurationRequest(
        businessId,
        buildCreateReportConfigurationPayload({
          businessId: Number(businessId),
          name,
          segmentId: Number(createForm.segmentId),
          sheetTagGroupId: createForm.sheetTagGroupId || undefined,
          sectionTagGroupId: createForm.sectionTagGroupId || undefined,
          planIds: createForm.planIds,
          ownerPlanGroupIds: createForm.ownerPlanGroupIds,
          contributionPlanGroupIds: createForm.contributionPlanGroupIds,
        })
      )
    );
  }, [dispatch, businessId, createForm]);

  useEffect(() => {
    if (
      prevCreatingRef.current &&
      !creatingReportConfiguration &&
      createModalOpen &&
      !errorCreateReportConfiguration
    ) {
      closeCreateModal();
      toast.current?.show({
        severity: "success",
        summary: t("success"),
        detail: t("reportConfigs.create_success"),
        life: 3000,
      });
    }
    prevCreatingRef.current = creatingReportConfiguration;
  }, [
    creatingReportConfiguration,
    errorCreateReportConfiguration,
    createModalOpen,
    closeCreateModal,
    t,
  ]);

  useEffect(() => {
    const wasGenerating = prevGeneratingIdRef.current != null;
    const isGenerating = generatingReportConfigurationId != null;

    if (wasGenerating && !isGenerating) {
      if (errorGenerateReportConfiguration) {
        toast.current?.show({
          severity: "error",
          summary: t("error.error"),
          detail: t("reportConfigs.generate_error"),
          life: 4000,
        });
      } else {
        toast.current?.show({
          severity: "success",
          summary: t("success"),
          detail: t("reportConfigs.generate_success"),
          life: 3000,
        });
      }
    }
    prevGeneratingIdRef.current = generatingReportConfigurationId;
  }, [generatingReportConfigurationId, errorGenerateReportConfiguration, t]);

  useEffect(() => {
    const wasDeleting = prevDeletingIdRef.current != null;
    const isDeleting = deletingReportConfigurationId != null;

    if (wasDeleting && !isDeleting) {
      if (errorDeleteReportConfiguration) {
        toast.current?.show({
          severity: "error",
          summary: t("error.error"),
          detail: t("reportConfigs.delete_error"),
          life: 4000,
        });
      } else {
        toast.current?.show({
          severity: "success",
          summary: t("success"),
          detail: t("reportConfigs.delete_success"),
          life: 3000,
        });
      }
    }
    prevDeletingIdRef.current = deletingReportConfigurationId;
  }, [deletingReportConfigurationId, errorDeleteReportConfiguration, t]);

  const tableData = useMemo(
    () =>
      mapReportConfigurationsForTable(reportConfigurations).map((row) => ({
        ...row,
        isGenerating:
          generatingReportConfigurationId != null &&
          String(generatingReportConfigurationId) === String(row.id),
        isDeleting:
          deletingReportConfigurationId != null &&
          String(deletingReportConfigurationId) === String(row.id),
      })),
    [reportConfigurations, generatingReportConfigurationId, deletingReportConfigurationId]
  );

  useEffect(() => {
    if (first >= tableData.length && tableData.length > 0) {
      setFirst(0);
    }
  }, [tableData.length, first]);

  const paginatedTableData = useMemo(
    () => tableData.slice(first, first + REPORT_CONFIG_ROWS),
    [tableData, first]
  );

  const isGenerateInProgress = generatingReportConfigurationId != null;
  const isDeleteInProgress = deletingReportConfigurationId != null;
  const isRowActionInProgress = isGenerateInProgress || isDeleteInProgress;

  const columnsBody = (row) => {
    const sections = getReportConfigurationColumnSections(row, businessCurrency);
    if (!sections.length) return null;
    return (
      <div className="report-configs__column-config text-left">
        {sections.map((section) => (
          <div key={section.key} className="report-configs__column-section">
            <div className="report-configs__column-section-title">
              {t(section.titleKey)}
            </div>
            <ul className="report-configs__column-plans-list">
              {section.items.map((item) => (
                <li key={`${section.key}-${item.id ?? item.label}`}>{item.label}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    );
  };

  const statusBody = (row) => (
    <StatusBadge status={row.active ? t("reportConfigs.active") : t("reportConfigs.inactive")} />
  );

  const actionsBody = (row) => (
    <div className="report-configs__actions">
      <Button
        icon="pi pi-download"
        className="p-button action-button"
        tooltip={
          row.isGenerating ? t("reportConfigs.generating") : t("reportConfigs.generate")
        }
        tooltipOptions={ACTION_TOOLTIP_OPTIONS}
        disabled={isRowActionInProgress}
        loading={row.isGenerating}
        onClick={() => handleGenerateReport(row.id, row.name)}
      />
      <Button
        icon="pi pi-trash"
        className="p-button action-button report-configs__delete-action"
        tooltip={
          row.isDeleting ? t("reportConfigs.deleting") : t("reportConfigs.delete")
        }
        tooltipOptions={ACTION_TOOLTIP_OPTIONS}
        disabled={isRowActionInProgress}
        loading={row.isDeleting}
        onClick={() => handleDeleteReport(row.id, row.name)}
      />
    </div>
  );

  const desktopColumns = [
    { field: "name", header: t("common.name"), alignHeader: "left" },
    { field: "segmentName", header: t("reportConfigs.segment"), alignHeader: "left" },
    { field: "sheetGroupName", header: t("reportConfigs.sheet_tag_group"), alignHeader: "left" },
    {
      field: "sectionGroupName",
      header: t("reportConfigs.section_tag_group"),
      alignHeader: "left",
      align: "left",
    },
    { field: "columnPlans", header: t("reportConfigs.columns"), body: columnsBody, alignHeader: "left" },
    { field: "active", header: t("common.status"), body: statusBody, alignHeader: "left" },
    { field: "actions", header: t("common.actions"), body: actionsBody, alignHeader: "left" },
  ];

  const contributionsMobileSummary = (row) => (
    <div className="contributions-report-mobile-summary">
      {desktopColumns.map((col, index) => {
        const isLast = index === desktopColumns.length - 1;
        return (
          <div
            key={col.field}
            className={`contributions-report-mobile-section${
              isLast ? " contributions-report-mobile-section-last" : ""
            }`}
          >
            {col.header ? (
              <span className="contributions-report-mobile-label">{col.header}</span>
            ) : null}
            {col.body ? col.body(row) : row[col.field]}
          </div>
        );
      })}
    </div>
  );

  const mobileSummaryColumn = {
    field: "mobileSummary",
    header: t("common.name"),
    body: contributionsMobileSummary,
    className: "contributions-report-mobile-summary-column",
  };

  const tableColumns = isMobileStack ? [mobileSummaryColumn] : desktopColumns;

  const tableHeader = (
    <div className="contributions-report-table-header">
      <Button
        className="import_button"
        icon="pi pi-plus-circle"
        label={t("reportConfigs.create_config")}
        onClick={openCreateModal}
      />
    </div>
  );

  return (
    <div className="products-screen contributions-report-page">
      <Toast ref={toast} />
      <ConfirmDialog />

      <CustomDataTable
        key={`contributions-report-${isMobileStack}`}
        value={paginatedTableData}
        loading={fetchingReportConfigurations}
        emptyMessage={
          fetchingReportConfigurations ? t("reportConfigs.loading") : t("common.no_data")
        }
        columns={tableColumns}
        header={tableHeader}
        responsiveLayout="stack"
        tableClassName="contributions-report-data-table"
      />

      {tableData.length > REPORT_CONFIG_ROWS && (
        <Paginator
          first={first}
          rows={REPORT_CONFIG_ROWS}
          totalRecords={tableData.length}
          onPageChange={(event) => setFirst(event.first)}
          className="custom-paginator contributions-report-paginator"
          template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
          currentPageReportTemplate="{first} - {last} of {totalRecords}"
        />
      )}

      <CreateReportConfigModal
        visible={createModalOpen}
        onHide={closeCreateModal}
        form={createForm}
        onFormChange={handleCreateFormChange}
        segments={segments}
        tagGroups={tagGroups}
        plans={businessPlans}
        planGroups={businessPlanGroups}
        fetchingOptions={fetchingOptions}
        onSubmit={handleCreateSubmit}
        creating={creatingReportConfiguration}
        submitAttempted={createSubmitAttempted}
        submitError={createSubmitAttempted && !!errorCreateReportConfiguration}
      />
    </div>
  );
};

export default ContributionsReport;
